import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import CommentPost from 'flarum/forum/components/CommentPost';
import Post from 'flarum/forum/components/Post';
import Link from 'flarum/common/components/Link';
import Icon from 'flarum/common/components/Icon';
import punctuateSeries from 'flarum/common/helpers/punctuateSeries';
import { handleOf } from './handle';

/**
 * Replies keep their names -- Task 5.5, and the other half of the reactions
 * decision. Reactors are a count; repliers are named, as handles.
 *
 * WHY THIS IS 5.5's PROBLEM AND NOT 5.8's. Hiding `posts.user` is what breaks
 * this, so the task that hides it owns the consequence.
 * `addMentionedByList.js:108-135` builds the "replied to this" footer by
 * calling `reply.user()` twice: once to deduplicate repliers, and once to name
 * them. With the relationship hidden, every `user()` returns undefined -- so
 * the deduplication collapses **all** repliers into one, and that one renders
 * through `username(undefined)` as "[Deleted]".
 *
 * Not a leak, but a plainly broken line of text, and one that would have made
 * the forum look worse than it is at exactly the commit that secured it.
 *
 * `mentionedBy` itself is deliberately left visible in `extend.php`: it points
 * at POSTS, not users, and since 5.3 every post carries its own handle. So the
 * footer can be rebuilt from data that is already there and already safe.
 */
export default function replies() {
  extend(CommentPost.prototype, 'footerItems', function (items) {
    /*
     * The key is `replies`, which is what addMentionedByList.js:147 actually
     * uses. Guessing it cost a cycle at 5.5: a wrong key makes `has()` false
     * forever, this returns early every time, and core's broken version renders
     * unchallenged -- a silent no-op that looks exactly like a change that did
     * not work.
     */
    if (!items.has('replies')) return;
    const post = this.attrs.post;
    const replies = post.mentionedBy?.();
    if (!replies || !replies.length) return;

    /*
     * Deduplicate by HANDLE rather than by user. Within one Issue a handle
     * identifies a participant exactly as well as a user id does -- that is
     * what a handle is -- so this is the same operation on the safe key. It is
     * also why every post in `mentionedBy` belongs to the same Issue as this
     * one: they are replies to it.
     */
    const seen = new Set();
    const unique = [];
    for (const reply of replies) {
      const handle = handleOf(reply);
      if (handle === null || seen.has(handle)) continue;
      seen.add(handle);
      unique.push(reply);
    }
    if (!unique.length) return;
    const limit = 4;
    const overLimit = unique.length > limit;
    const shown = unique.slice(0, overLimit ? limit - 1 : limit);
    const names = shown.map(reply => m(Link, {
      href: app.route.post(reply),
      "data-number": reply.number()
    }, handleOf(reply)));
    if (overLimit) {
      names.push(app.translator.trans('core.ref.some_others', {
        count: unique.length - names.length
      }));
    }
    items.setContent('replies', m("div", {
      className: "Post-mentionedBy"
    }, m(Icon, {
      name: "fas fa-reply"
    }), app.translator.trans('flarum-mentions.forum.post.mentioned_by_text', {
      count: unique.length,
      users: punctuateSeries(names)
    })));
  });

  /*
   * The thread-starter highlight, restored.
   *
   * `Post.tsx:36` marks a starter's own posts with `Post--by-start-user` by
   * comparing `post.user()` to `discussion.user()`. Both are hidden now, so the
   * comparison is always false and the cue silently disappears. Comparing
   * handles means exactly the same thing -- both are per-Issue, and within an
   * Issue a handle identifies a person -- while revealing nothing.
   *
   * Cosmetic, and included here because a cue that quietly stops working is
   * worse than one that was never there: the next person would have no reason
   * to suspect it.
   */
  extend(Post.prototype, 'classes', function (classes) {
    const post = this.attrs.post;
    const discussion = post.discussion?.();
    if (!discussion) return;
    const postHandle = handleOf(post);
    const starterHandle = handleOf(discussion);
    if (postHandle !== null && postHandle === starterHandle && !classes.includes('Post--by-start-user')) {
      classes.push('Post--by-start-user');
    }
  });
}